import { useState } from "react";
import { useTranslation } from "react-i18next";

import { IGlucoseTimeInRange } from "../../../@types/Measurements";
import { useUserPreferences } from "../../../hooks/useUserPreferences";
import { StyledTooltip } from "../../CustomRechartsTooltip/CustomRechartsTooltip.styles";
import {
  formatTimeInRangePercent,
  getGlucoseUnit,
  getPregnancyInRangeLevel,
  getTimeInRangeLevel,
  TIME_IN_RANGE_BANDS,
  TimeInRangeBand,
} from "./timeInRange";
import {
  Bar,
  Container,
  Legend,
  LegendItem,
  Segment,
  Swatch,
  TooltipPosition,
} from "./TimeInRangeSummary.styles";

type TimeInRangeSummaryProps = {
  timeInRange: IGlucoseTimeInRange;
  /** First to last reading in the selected range. */
  dateRange: string;
};

export function TimeInRangeSummary({
  timeInRange,
  dateRange,
}: TimeInRangeSummaryProps) {
  const { t } = useTranslation();
  const { unitPreferences } = useUserPreferences();
  const unit = getGlucoseUnit(unitPreferences?.bloodGlucoseUnit);
  const [activeBand, setActiveBand] = useState<TimeInRangeBand | null>(null);

  const getTitle = (band: TimeInRangeBand, labelKey: string) =>
    t("chart_timeInRange_tooltipTitle", {
      percent: formatTimeInRangePercent(timeInRange[band]),
      band: t(labelKey),
    });

  const getLevel = (band: TimeInRangeBand) =>
    band === "inRange"
      ? t("chart_timeInRange_inRangeLevel", {
          standard: getTimeInRangeLevel(band, unit),
          pregnancy: getPregnancyInRangeLevel(unit),
        })
      : getTimeInRangeLevel(band, unit);

  const active = TIME_IN_RANGE_BANDS.find(({ band }) => band === activeBand);

  return (
    <Container>
      <Bar onMouseLeave={() => setActiveBand(null)}>
        {TIME_IN_RANGE_BANDS.filter(({ band }) => timeInRange[band] > 0).map(
          ({ band, labelKey }) => (
            <Segment
              key={band}
              band={band}
              // Proportional height is data, not style.
              style={{ flexGrow: timeInRange[band] }}
              tabIndex={0}
              role="img"
              aria-label={`${getTitle(band, labelKey)}, ${getLevel(
                band
              )}, ${dateRange}`}
              onMouseEnter={() => setActiveBand(band)}
              onFocus={() => setActiveBand(band)}
              onBlur={() => setActiveBand(null)}
            />
          )
        )}
      </Bar>

      <Legend>
        {TIME_IN_RANGE_BANDS.map(({ band, labelKey }) => (
          <LegendItem key={band}>
            <Swatch band={band} />
            {t(labelKey)}
          </LegendItem>
        ))}
      </Legend>

      {active && (
        <TooltipPosition role="tooltip">
          <StyledTooltip>
            <h4>{getTitle(active.band, active.labelKey)}</h4>
            <h4>{getLevel(active.band)}</h4>
            <p>{dateRange}</p>
          </StyledTooltip>
        </TooltipPosition>
      )}
    </Container>
  );
}
